import { motion, useReducedMotion } from "framer-motion";
import { IMG_MANIFEST } from "../data/imageManifest";

export const EASE = [0.22, 1, 0.36, 1];

// First-paint rule: prerendered HTML already contains final visible states, so
// on the very first client boot NOTHING may re-hide for an entrance animation
// (that re-hide was the "blank headings for 2-4s" bug). Entrance animations
// resume on subsequent client-side route navigations.
let booted = false;
if (typeof window !== "undefined") {
  requestAnimationFrame(() => { booted = true; });
}
export const hasBooted = () => booted;

// Fast content reveal — headlines land in ≤600ms, copy/CTAs in ≤500ms.
// Content is never held at opacity 0 waiting on scroll progress: reveals are
// whileInView/once with a generous margin, and reduced-motion users get
// everything instantly (no transforms, no scroll-jacked sequences).
export function Reveal({ children, delay = 0, y = 26, className = "", once = true, as = "div" }) {
  const reduce = useReducedMotion();
  const Tag = motion[as] || motion.div;
  const Plain = as; // keep semantic tags (li etc.) even when motion is reduced
  if (reduce) return <Plain className={className}>{children}</Plain>;
  return (
    <Tag
      initial={hasBooted() ? { opacity: 0, y } : false}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: "-8% 0px" }}
      transition={{ delay: Math.min(delay, 0.3), duration: 0.45, ease: EASE }}
      className={className}
    >
      {children}
    </Tag>
  );
}

// Masked line reveal for big editorial headlines — fast mount reveal
// (page-opening headlines land in ≤600ms; never gated behind scroll
// position, so text can never get stuck hidden mid-page).
export function MaskedLines({ lines, className = "", as = "h2", accent = [], accentTone = "text-dcc" }) {
  const reduce = useReducedMotion();
  const Tag = motion[as] || motion.h2;
  return (
    <Tag className={className}>
      {lines.map((line, i) => (
        <span key={line} className="block overflow-hidden">
          <motion.span
            initial={reduce || !hasBooted() ? false : { y: "112%" }}
            animate={reduce ? undefined : { y: "0%" }}
            transition={{ delay: 0.1 + i * 0.08, duration: 0.5, ease: EASE }}
            className={`block will-change-transform ${accent.includes(i) ? accentTone : ""}`}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
}

// Framed wipe-in for imagery. Intrinsic width/height (+ responsive srcSet/sizes
// when generated variants exist) auto-applied from the image manifest — every
// content image is CLS-safe without call-site bookkeeping.
// eager=true (heroes/above-fold): fetch immediately with high priority and
// never re-hide on boot — a lazy+animated hero was the 11s mobile LCP.
export function ClipImage({ src, alt, ratio = "aspect-[4/3]", className = "", pos = "center", delay = 0, sizes, width, height, eager = false }) {
  const reduce = useReducedMotion();
  const dim = IMG_MANIFEST[src];
  const srcSet = dim?.v && src.endsWith(".webp")
    ? [...dim.v.map((w) => `${src.replace(/\.webp$/, `-${w}.webp`)} ${w}w`), `${src} ${dim.w}w`].join(", ")
    : undefined;
  const noEntrance = reduce || !hasBooted() || eager;
  return (
    <div className={`overflow-hidden ${ratio} ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        width={width || dim?.w}
        height={height || dim?.h}
        srcSet={srcSet}
        sizes={sizes || (srcSet ? "(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 640px" : undefined)}
        loading={eager ? "eager" : "lazy"}
        fetchpriority={eager ? "high" : "auto"}
        decoding="async"
        initial={noEntrance ? false : { scale: 1.08, opacity: 0.4 }}
        whileInView={noEntrance ? undefined : { scale: 1, opacity: 1 }}
        viewport={{ once: true, margin: "-6% 0px" }}
        transition={{ delay: Math.min(delay, 0.3), duration: 0.5, ease: EASE }}
        style={{ objectPosition: pos }}
        className="h-full w-full object-cover will-change-transform"
      />
    </div>
  );
}
